/**
 * WDW Leistungs-Bewertungen, Client-Fallback: lädt die Judge.me-Bewertungen eines Produkts über den
 * öffentlichen Widget-Endpunkt von Judge.me, wenn das Produkt für Liquid nicht erreichbar ist (nicht im
 * Onlineshop-Kanal). Das Markup ist dasselbe, das Judge.me selbst ins Widget schreibt, deshalb greift
 * wdw-service-reviews.css unverändert. Zusätzlich: Durchschnitt und Anzahl für die Zusammenfassung,
 * Datum im deutschen Format. Ohne Bewertungen oder bei Fehlern bleibt die Section ausgeblendet.
 */
const ENDPOINT = 'https://judge.me/reviews/reviews_for_widget';
const PER_PAGE = 50;

class WdwServiceReviews extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    // Sofort laden: die Section ist bis zum Ergebnis ausgeblendet, ein IntersectionObserver würde deshalb nie auslösen.
    if (!this.dataset.productId || !this.dataset.shopDomain) return;
    this.#load();
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const params = new URLSearchParams({
      url: this.dataset.shopDomain,
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      product_id: this.dataset.productId,
      per_page: String(PER_PAGE),
      page: '1',
    });

    try {
      const response = await fetch(`${ENDPOINT}?${params}`, { credentials: 'omit' });
      if (!response.ok) throw new Error(`Judge.me antwortet mit ${response.status}`);
      const data = await response.json();
      const total = Number(data.total_count) || 0;
      if (total === 0 || !data.html) {
        this.#setStatus('Noch keine Judge.me-Bewertungen für dieses Produkt.');
        return;
      }
      this.#render(data.html, total);
    } catch (error) {
      this.#setStatus(`Bewertungen konnten nicht geladen werden (${error.message}).`);
    }
  }

  /**
   * @param {string} html - Widget-HTML von Judge.me
   * @param {number} total - Gesamtzahl der Bewertungen
   */
  #render(html, total) {
    const target = this.querySelector('[data-reviews]');
    if (!target) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove());

    const scores = [...doc.querySelectorAll('.jdgm-rev__rating[data-score]')].map((el) => Number(el.dataset.score) || 0);
    const average = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;

    doc.querySelectorAll('time.jdgm-rev__timestamp[datetime]').forEach((time) => {
      const date = new Date(time.getAttribute('datetime') || '');
      if (!Number.isNaN(date.getTime())) {
        time.textContent = date.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
      }
      time.classList.remove('jdgm-spinner');
    });

    target.replaceChildren(...doc.body.childNodes);
    this.#renderSummary(average, total);
    this.#setStatus('');
    this.closest('.wdw-service-reviews')?.classList.remove('wdw-service-reviews--pending');
  }

  /**
   * @param {number} average - Durchschnitt 0–5
   * @param {number} total - Anzahl Bewertungen
   */
  #renderSummary(average, total) {
    const averageEl = this.querySelector('[data-average]');
    const starsEl = this.querySelector('[data-stars]');
    const countEl = this.querySelector('[data-count]');
    const rounded = Math.round(average * 10) / 10;

    if (averageEl) averageEl.textContent = rounded.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (starsEl) {
      starsEl.setAttribute('aria-label', `${rounded.toLocaleString('de-DE')} von 5 Sternen`);
      starsEl.replaceChildren(
        ...Array.from({ length: 5 }, (_, index) => {
          const star = document.createElement('span');
          const fill = average - index;
          star.className = `jdgm-star ${fill >= 0.75 ? 'jdgm--on' : fill >= 0.25 ? 'jdgm--half' : 'jdgm--off'}`;
          return star;
        })
      );
    }
    if (countEl) countEl.textContent = `Basierend auf ${total} ${total === 1 ? 'Bewertung' : 'Bewertungen'}`;
  }

  /** @param {string} text - Hinweis, nur im Theme-Editor sichtbar */
  #setStatus(text) {
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = text;
  }
}

if (!customElements.get('wdw-service-reviews')) {
  customElements.define('wdw-service-reviews', WdwServiceReviews);
}
